// Settings — the whole of the old View menu, plus sound (PLAN-UI §28.3).
//
// It was a `▾` menu anchored to the shelf strip, which is where a *command*
// belongs and not where a preference does: a menu closes on every click, so
// setting two related things meant reopening it, and the four view actions and
// the five things the game remembers looked like one list of nine.
//
// Here they are two groups with a rule between them. The actions still close
// the menu — you clicked them to look at the board — and every toggle leaves it
// open.

import { SPEEDS, snapSpeed } from "src/client/ai/render/tempo";

import type { CameraPrefs } from "../cameraPrefs";
import { Btn, SectionTitle, SettingRow, Slider, StatCounter, Toggle, styled } from "../kit";
import { SoundSettings } from "../SoundSettings";
import { RowLabel, ScreenTitle } from "./text";

export interface SettingsScreenProps {
  camera: CameraPrefs;
  onCamera(patch: Partial<CameraPrefs>): void;
  /** The classic board, where the camera has nowhere to go. */
  cameraFixed: boolean;
  onResetView(): void;
  /** Closes the menu — a view action is about looking at the board. */
  onClose(): void;
}

const Actions = styled("div")(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: theme.kit.space.sm,
  margin: `${theme.kit.space.sm}px 0 ${theme.kit.space.lg}px`
}));

/** The slider and its number, on the right of the row it labels. */
const SpeedControl = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.kit.space.md,
  width: 220
}));

const Group = styled("div")(({ theme }) => ({
  marginTop: theme.kit.space.lg,
  marginBottom: theme.kit.space.xs
}));

export function SettingsScreen({
  camera,
  onCamera,
  cameraFixed,
  onResetView,
  onClose
}: SettingsScreenProps) {
  const act = (run: () => void) => () => {
    run();
    onClose();
  };
  // Snapped here as well as on the way out of storage, so the slider always
  // has an index to sit on: `indexOf` answers -1 for anything off the ladder,
  // and a slider at -1 is a control drawn off the end of its own track.
  const speed = snapSpeed(camera.animationSpeed);

  return (
    <div data-testid="pause-screen-settings">
      <ScreenTitle>Settings</ScreenTitle>

      {/*
        **One action, because the rest are on the rail now.** Turn, tilt and
        fit are camera controls, and a camera control two clicks away behind a
        menu is one nobody uses mid-game — they hang under the tools where the
        board is (`viewActions.ts`). Reset stays here: it is the way out of a
        view you have got lost in rather than a way of looking around, and
        `view-centre` went with it, since a fit does the same job better.
      */}
      <SectionTitle>View</SectionTitle>
      <Actions>
        <Btn small data-testid="view-reset" onClick={act(onResetView)}>
          Reset the view
        </Btn>
      </Actions>

      {/*
        Whether anything but your own drag may move the view. Two rows rather
        than one because there are two complaints: the camera leads to every
        enemy move, which is what reads as being flown rather than playing, and
        it also leads to a front landing, which is the rare large case it was
        built for.

        Both go quiet on the classic board — there is no off-screen ground for
        an offset to reach, so the camera never moves anyway.
      */}
      <SettingRow>
        <RowLabel off={cameraFixed}>Move the camera for me</RowLabel>
        <Toggle
          data-testid="camera-auto"
          disabled={cameraFixed}
          checked={camera.autoFocus && !cameraFixed}
          onChange={() => onCamera({ autoFocus: !camera.autoFocus })}
          aria-label="Move the camera for me"
        />
      </SettingRow>
      <SettingRow>
        <RowLabel off={cameraFixed || !camera.autoFocus}>
          Follow the piece that moved
        </RowLabel>
        <Toggle
          data-testid="camera-track"
          disabled={cameraFixed || !camera.autoFocus}
          checked={camera.trackPiece && camera.autoFocus && !cameraFixed}
          onChange={() => onCamera({ trackPiece: !camera.trackPiece })}
          aria-label="Follow the piece that moved"
        />
      </SettingRow>
      {cameraFixed && (
        <SettingRow data-testid="camera-fixed">
          <RowLabel off>The whole board is on screen</RowLabel>
        </SettingRow>
      )}

      {/*
        How the board is *drawn*, rather than where the camera goes — which is
        why this is not silenced with the two above. Those are about reaching
        ground that is off screen; an eight-by-eight has none, and has a look.
      */}
      <SettingRow>
        <RowLabel>Perspective camera</RowLabel>
        <Toggle
          data-testid="camera-perspective"
          checked={camera.perspective}
          onChange={() => onCamera({ perspective: !camera.perspective })}
          aria-label="Perspective camera"
        />
      </SettingRow>

      {/*
        What it relaxes is the *ordering*, not the speed: the opponent's turn
        goes out in one batch and the factory's beat runs over the move instead
        of behind it. Not silenced on the classic board either — an
        eight-by-eight has an opponent taking turns like everywhere else.
      */}
      <SettingRow>
        <RowLabel>Opponent plays fast</RowLabel>
        <Toggle
          data-testid="opponent-fast"
          checked={camera.opponentFast}
          onChange={() => onCamera({ opponentFast: !camera.opponentFast })}
          aria-label="Opponent plays fast"
        />
      </SettingRow>

      {/*
        The one speed control that belongs to the player rather than to the
        situation: it applies in every mode and is the only one that scales
        your own turn. The multiplier is printed because a slider with no
        number is a control you set by feel and cannot repeat.
      */}
      <SettingRow data-testid="camera-speed">
        <RowLabel>Animation speed</RowLabel>
        <SpeedControl>
          <Slider
            size="small"
            // Indexed over the ladder rather than valued on it: the stops are
            // not evenly spaced (a quarter, a half, then whole multiples), and
            // a slider laid out by value would give the eight-times end most of
            // the track for the setting nobody spends time in.
            value={SPEEDS.indexOf(speed as (typeof SPEEDS)[number])}
            min={0}
            max={SPEEDS.length - 1}
            step={1}
            marks
            aria-label="Animation speed"
            valueLabelDisplay="off"
            onChange={(_, value) =>
              onCamera({ animationSpeed: SPEEDS[value as number] })
            }
          />
          <StatCounter data-testid="camera-speed-value" style={{ fontSize: 13 }}>
            {speed}×
          </StatCounter>
        </SpeedControl>
      </SettingRow>

      {/* Embedded as it stands, its own "SOUND" caption included — it holds
          its own prefs and pushes them into the engine itself, so it moves
          surfaces without a refactor. U9 rebases it onto the kit. */}
      <Group>
        <SoundSettings />
      </Group>
    </div>
  );
}
